// Shape table: parameter schema + Canvas2D path builders + extents.
//
// Every shape is described once here. The editor renders its controls from
// `params`, validation clamps against `params`, the renderer uses `draw`, and
// canvas sizing uses `extent`. Adding a shape is one entry.

import type { ParamValue, ShapeId } from "./types";

export interface ParamOption {
  value: string;
  label: string;
}

export interface ParamDef {
  key: string;
  label: string;
  kind: "num" | "bool" | "enum";
  def: ParamValue;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  options?: ParamOption[];
  tip?: string;
}

export interface ShapeDef {
  id: ShapeId;
  label: string;
  params: ParamDef[];
  /** Conservative radius of the shape from the centre, in local units. */
  extent: (p: Record<string, ParamValue>) => number;
  draw: (ctx: CanvasRenderingContext2D, p: Record<string, ParamValue>) => void;
  filled: (p: Record<string, ParamValue>) => boolean;
  /** Stroke width when not filled. */
  width: (p: Record<string, ParamValue>) => number;
  round: (p: Record<string, ParamValue>) => boolean;
}

const num = (p: Record<string, ParamValue>, k: string, d = 0): number =>
  typeof p[k] === "number" && Number.isFinite(p[k]) ? (p[k] as number) : d;
const on = (p: Record<string, ParamValue>, k: string): boolean => p[k] === true;

const DEG = Math.PI / 180;

const cross: ShapeDef = {
  id: "cross",
  label: "Cross",
  params: [
    { key: "top", label: "Top", kind: "bool", def: true },
    { key: "right", label: "Right", kind: "bool", def: true },
    { key: "bottom", label: "Bottom", kind: "bool", def: true },
    { key: "left", label: "Left", kind: "bool", def: true },
    { key: "length", label: "Length", kind: "num", def: 8, min: 0, max: 64, step: 0.5, unit: "px" },
    { key: "thickness", label: "Thickness", kind: "num", def: 2, min: 0.5, max: 16, step: 0.5, unit: "px" },
    { key: "gap", label: "Gap", kind: "num", def: 0, min: 0, max: 64, step: 0.5, unit: "px" },
    { key: "round", label: "Round caps", kind: "bool", def: false },
  ],
  extent: (p) => num(p, "gap") + num(p, "length") + num(p, "thickness", 2) / 2,
  width: (p) => num(p, "thickness", 2),
  filled: () => false,
  round: (p) => on(p, "round"),
  draw: (ctx, p) => {
    const gap = num(p, "gap");
    const len = num(p, "length");
    const a = gap;
    const b = gap + len;
    if (on(p, "top")) {
      ctx.moveTo(0, -a);
      ctx.lineTo(0, -b);
    }
    if (on(p, "bottom")) {
      ctx.moveTo(0, a);
      ctx.lineTo(0, b);
    }
    if (on(p, "left")) {
      ctx.moveTo(-a, 0);
      ctx.lineTo(-b, 0);
    }
    if (on(p, "right")) {
      ctx.moveTo(a, 0);
      ctx.lineTo(b, 0);
    }
  },
};

const dot: ShapeDef = {
  id: "dot",
  label: "Dot",
  params: [
    { key: "size", label: "Size", kind: "num", def: 4, min: 0.5, max: 32, step: 0.5, unit: "px" },
    { key: "square", label: "Square", kind: "bool", def: false },
    { key: "hollow", label: "Hollow", kind: "bool", def: false },
    { key: "thickness", label: "Ring thickness", kind: "num", def: 1, min: 0.5, max: 8, step: 0.5, unit: "px" },
  ],
  extent: (p) => num(p, "size", 4) / 2 + 1,
  width: (p) => num(p, "thickness", 1),
  filled: (p) => !on(p, "hollow"),
  round: () => false,
  draw: (ctx, p) => {
    const r = num(p, "size", 4) / 2;
    if (on(p, "square")) {
      ctx.rect(-r, -r, r * 2, r * 2);
    } else {
      ctx.arc(0, 0, r, 0, Math.PI * 2);
    }
  },
};

const ring: ShapeDef = {
  id: "ring",
  label: "Ring / arc",
  params: [
    { key: "radius", label: "Radius", kind: "num", def: 16, min: 1, max: 96, step: 0.5, unit: "px" },
    { key: "thickness", label: "Thickness", kind: "num", def: 2, min: 0.5, max: 16, step: 0.5, unit: "px" },
    { key: "sides", label: "Sides (0 = circle)", kind: "num", def: 0, min: 0, max: 12, step: 1 },
    { key: "start", label: "Start angle", kind: "num", def: 0, min: -360, max: 360, step: 1, unit: "°" },
    { key: "sweep", label: "Sweep", kind: "num", def: 360, min: 1, max: 360, step: 1, unit: "°" },
    { key: "segments", label: "Segments", kind: "num", def: 1, min: 1, max: 24, step: 1 },
    { key: "segmentGap", label: "Segment gap", kind: "num", def: 12, min: 0, max: 90, step: 1, unit: "°" },
    { key: "round", label: "Round caps", kind: "bool", def: false },
  ],
  extent: (p) => num(p, "radius", 16) + num(p, "thickness", 2) / 2 + 1,
  width: (p) => num(p, "thickness", 2),
  filled: () => false,
  round: (p) => on(p, "round"),
  draw: (ctx, p) => {
    const r = num(p, "radius", 16);
    const sides = Math.round(num(p, "sides"));
    const start = num(p, "start") * DEG;
    const sweep = num(p, "sweep", 360) * DEG;
    const segs = Math.max(1, Math.round(num(p, "segments", 1)));
    if (sides >= 3) {
      // Regular polygon ring (full or partial sweep; closed when full).
      const full = sweep >= Math.PI * 2 - 1e-6;
      const step = full ? sweep / sides : sweep / Math.max(1, sides - 1);
      for (let i = 0; i < sides; i++) {
        const a = start + i * step;
        const x = Math.cos(a) * r;
        const y = Math.sin(a) * r;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      if (full) ctx.closePath();
      return;
    }
    if (segs <= 1) {
      ctx.arc(0, 0, r, start, start + sweep);
      return;
    }
    const gap = Math.min(num(p, "segmentGap", 12) * DEG, (sweep / segs) * 0.9);
    const each = (sweep - gap * (segs - 1)) / segs;
    for (let i = 0; i < segs; i++) {
      const a0 = start + i * (each + gap);
      ctx.moveTo(Math.cos(a0) * r, Math.sin(a0) * r);
      ctx.arc(0, 0, r, a0, a0 + each);
    }
  },
};

const line: ShapeDef = {
  id: "line",
  label: "Line",
  params: [
    { key: "length", label: "Length", kind: "num", def: 16, min: 0.5, max: 128, step: 0.5, unit: "px" },
    { key: "thickness", label: "Thickness", kind: "num", def: 2, min: 0.5, max: 16, step: 0.5, unit: "px" },
    { key: "round", label: "Round caps", kind: "bool", def: false },
  ],
  extent: (p) => num(p, "length", 16) / 2 + num(p, "thickness", 2) / 2 + 1,
  width: (p) => num(p, "thickness", 2),
  filled: () => false,
  round: (p) => on(p, "round"),
  draw: (ctx, p) => {
    const h = num(p, "length", 16) / 2;
    ctx.moveTo(-h, 0);
    ctx.lineTo(h, 0);
  },
};

const polygon: ShapeDef = {
  id: "polygon",
  label: "Polygon",
  params: [
    { key: "sides", label: "Sides", kind: "num", def: 3, min: 3, max: 12, step: 1 },
    { key: "radius", label: "Radius", kind: "num", def: 16, min: 2, max: 96, step: 0.5, unit: "px" },
    { key: "thickness", label: "Thickness", kind: "num", def: 2, min: 0.5, max: 16, step: 0.5, unit: "px" },
    { key: "filled", label: "Filled", kind: "bool", def: false },
    { key: "round", label: "Round joins", kind: "bool", def: false },
  ],
  extent: (p) => num(p, "radius", 16) + num(p, "thickness", 2) / 2 + 1,
  width: (p) => num(p, "thickness", 2),
  filled: (p) => on(p, "filled"),
  round: (p) => on(p, "round"),
  draw: (ctx, p) => {
    const sides = Math.max(3, Math.round(num(p, "sides", 3)));
    const r = num(p, "radius", 16);
    // Point up.
    for (let i = 0; i < sides; i++) {
      const a = -Math.PI / 2 + (i / sides) * Math.PI * 2;
      const x = Math.cos(a) * r;
      const y = Math.sin(a) * r;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
  },
};

const chevron: ShapeDef = {
  id: "chevron",
  label: "Chevron",
  params: [
    { key: "length", label: "Arm length", kind: "num", def: 10, min: 1, max: 64, step: 0.5, unit: "px" },
    { key: "thickness", label: "Thickness", kind: "num", def: 2, min: 0.5, max: 16, step: 0.5, unit: "px" },
    { key: "angle", label: "Opening", kind: "num", def: 90, min: 10, max: 170, step: 1, unit: "°" },
    { key: "arms", label: "Arms (1-4)", kind: "num", def: 1, min: 1, max: 4, step: 1 },
    { key: "round", label: "Round joins", kind: "bool", def: false },
  ],
  extent: (p) => num(p, "length", 10) + num(p, "thickness", 2) / 2 + 1,
  width: (p) => num(p, "thickness", 2),
  filled: () => false,
  round: (p) => on(p, "round"),
  draw: (ctx, p) => {
    const len = num(p, "length", 10);
    const half = (num(p, "angle", 90) / 2) * DEG;
    const arms = Math.max(1, Math.min(4, Math.round(num(p, "arms", 1))));
    const w = Math.sin(half) * len;
    const h = Math.cos(half) * len;
    for (let i = 0; i < arms; i++) {
      const a = i * (Math.PI / 2);
      const cos = Math.cos(a);
      const sin = Math.sin(a);
      const px = (x: number, y: number): [number, number] => [x * cos - y * sin, x * sin + y * cos];
      const a1 = px(-w, -h);
      const a2 = px(w, -h);
      ctx.moveTo(a1[0], a1[1]);
      ctx.lineTo(0, 0);
      ctx.lineTo(a2[0], a2[1]);
    }
  },
};

const bracket: ShapeDef = {
  id: "bracket",
  label: "Bracket",
  params: [
    { key: "length", label: "Arm length", kind: "num", def: 8, min: 1, max: 64, step: 0.5, unit: "px" },
    { key: "thickness", label: "Thickness", kind: "num", def: 2, min: 0.5, max: 16, step: 0.5, unit: "px" },
    { key: "corners", label: "Corners (1/2/4)", kind: "num", def: 4, min: 1, max: 4, step: 1 },
    { key: "round", label: "Round joins", kind: "bool", def: false },
  ],
  extent: (p) => num(p, "length", 8) + num(p, "thickness", 2) / 2 + 1,
  width: (p) => num(p, "thickness", 2),
  filled: () => false,
  round: (p) => on(p, "round"),
  draw: (ctx, p) => {
    const len = num(p, "length", 8);
    const corners = Math.round(num(p, "corners", 4));
    const pts: [number, number][] =
      corners <= 1
        ? [[-len, -len]]
        : corners === 2
          ? [
              [-len, -len],
              [len, -len],
            ]
          : [
              [-len, -len],
              [len, -len],
              [-len, len],
              [len, len],
            ];
    for (const [x, y] of pts) {
      const sx = Math.sign(x);
      const sy = Math.sign(y);
      ctx.moveTo(x - sx * len, y);
      ctx.lineTo(x, y);
      ctx.lineTo(x, y - sy * len);
    }
  },
};

const box: ShapeDef = {
  id: "box",
  label: "Box",
  params: [
    { key: "width", label: "Width", kind: "num", def: 16, min: 1, max: 128, step: 0.5, unit: "px" },
    { key: "height", label: "Height", kind: "num", def: 16, min: 1, max: 128, step: 0.5, unit: "px" },
    { key: "thickness", label: "Thickness", kind: "num", def: 2, min: 0.5, max: 16, step: 0.5, unit: "px" },
    { key: "radius", label: "Corner radius", kind: "num", def: 0, min: 0, max: 32, step: 0.5, unit: "px" },
    { key: "filled", label: "Filled", kind: "bool", def: false },
  ],
  extent: (p) => Math.hypot(num(p, "width", 16), num(p, "height", 16)) / 2 + num(p, "thickness", 2) / 2 + 1,
  width: (p) => num(p, "thickness", 2),
  filled: (p) => on(p, "filled"),
  round: () => false,
  draw: (ctx, p) => {
    const w = num(p, "width", 16);
    const h = num(p, "height", 16);
    const r = Math.min(num(p, "radius"), Math.min(w, h) / 2);
    if (typeof ctx.roundRect === "function") ctx.roundRect(-w / 2, -h / 2, w, h, r);
    else ctx.rect(-w / 2, -h / 2, w, h);
  },
};

const ticks: ShapeDef = {
  id: "ticks",
  label: "Ticks",
  params: [
    { key: "count", label: "Count", kind: "num", def: 4, min: 1, max: 24, step: 1 },
    { key: "radius", label: "Radius", kind: "num", def: 18, min: 0, max: 96, step: 0.5, unit: "px" },
    { key: "length", label: "Length", kind: "num", def: 6, min: 0.5, max: 64, step: 0.5, unit: "px" },
    { key: "thickness", label: "Thickness", kind: "num", def: 2, min: 0.5, max: 16, step: 0.5, unit: "px" },
    { key: "start", label: "Start angle", kind: "num", def: 0, min: -360, max: 360, step: 1, unit: "°" },
    { key: "spread", label: "Spread", kind: "num", def: 360, min: 0, max: 360, step: 1, unit: "°" },
    { key: "round", label: "Round caps", kind: "bool", def: false },
  ],
  extent: (p) => num(p, "radius", 18) + num(p, "length", 6) + num(p, "thickness", 2) / 2 + 1,
  width: (p) => num(p, "thickness", 2),
  filled: () => false,
  round: (p) => on(p, "round"),
  draw: (ctx, p) => {
    const count = Math.max(1, Math.round(num(p, "count", 4)));
    const r0 = num(p, "radius", 18);
    const r1 = r0 + num(p, "length", 6);
    const start = num(p, "start") * DEG;
    const spread = num(p, "spread", 360) * DEG;
    const full = count > 1 && Math.abs(spread) >= Math.PI * 2 - 1e-6;
    const step = full ? spread / count : spread / Math.max(1, count - 1);
    for (let i = 0; i < count; i++) {
      const a = start + i * step;
      ctx.moveTo(Math.cos(a) * r0, Math.sin(a) * r0);
      ctx.lineTo(Math.cos(a) * r1, Math.sin(a) * r1);
    }
  },
};

const star: ShapeDef = {
  id: "star",
  label: "Star",
  params: [
    { key: "points", label: "Points", kind: "num", def: 5, min: 3, max: 16, step: 1 },
    { key: "radius", label: "Outer radius", kind: "num", def: 16, min: 2, max: 96, step: 0.5, unit: "px" },
    { key: "inner", label: "Inner ratio", kind: "num", def: 0.5, min: 0.05, max: 0.95, step: 0.05 },
    { key: "thickness", label: "Thickness", kind: "num", def: 2, min: 0.5, max: 16, step: 0.5, unit: "px" },
    { key: "filled", label: "Filled", kind: "bool", def: false },
    { key: "round", label: "Round joins", kind: "bool", def: false },
  ],
  extent: (p) => num(p, "radius", 16) + num(p, "thickness", 2) / 2 + 1,
  width: (p) => num(p, "thickness", 2),
  filled: (p) => on(p, "filled"),
  round: (p) => on(p, "round"),
  draw: (ctx, p) => {
    const points = Math.max(3, Math.round(num(p, "points", 5)));
    const r = num(p, "radius", 16);
    const inner = Math.max(0.05, Math.min(0.95, num(p, "inner", 0.5))) * r;
    for (let i = 0; i < points * 2; i++) {
      const a = -Math.PI / 2 + (i / (points * 2)) * Math.PI * 2;
      const rr = i % 2 === 0 ? r : inner;
      const x = Math.cos(a) * rr;
      const y = Math.sin(a) * rr;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
  },
};

export const SHAPE_DEFS: Record<ShapeId, ShapeDef> = {
  cross,
  dot,
  ring,
  line,
  polygon,
  chevron,
  bracket,
  box,
  ticks,
  star,
};

export const SHAPE_IDS = Object.keys(SHAPE_DEFS) as ShapeId[];

/** Default params for a shape (a fresh object every call). */
export function shapeDefaults(id: ShapeId): Record<string, ParamValue> {
  const out: Record<string, ParamValue> = {};
  for (const d of SHAPE_DEFS[id].params) out[d.key] = d.def;
  return out;
}

/** Clamp/sanitize a params bag against a shape's schema. */
export function normalizeParams(
  id: ShapeId,
  raw: Record<string, unknown> | undefined,
): Record<string, ParamValue> {
  const out = shapeDefaults(id);
  if (!raw || typeof raw !== "object") return out;
  for (const d of SHAPE_DEFS[id].params) {
    const v = raw[d.key];
    if (d.kind === "bool") {
      if (typeof v === "boolean") out[d.key] = v;
    } else if (d.kind === "num") {
      if (typeof v === "number" && Number.isFinite(v)) {
        out[d.key] = Math.min(d.max ?? Infinity, Math.max(d.min ?? -Infinity, v));
      }
    } else if (typeof v === "string" && (d.options ?? []).some((o) => o.value === v)) {
      out[d.key] = v;
    }
  }
  return out;
}

/** Shape icon path (24×24 viewBox) for tabs/palette buttons. */
export const SHAPE_ICONS: Record<ShapeId, string> = {
  cross: "M12 3v6M12 15v6M3 12h6M15 12h6",
  dot: "M12 10a2 2 0 1 0 0 4a2 2 0 1 0 0-4",
  ring: "M12 4a8 8 0 1 0 0 16a8 8 0 1 0 0-16",
  line: "M5 12h14",
  polygon: "M12 3l8 6-3 9H7l-3-9z",
  chevron: "M5 15l7-7 7 7",
  bracket: "M5 9V5h4M15 5h4v4M19 15v4h-4M9 19H5v-4",
  box: "M4 4h16v16H4z",
  ticks: "M12 2v5M12 17v5M22 12h-5M7 12H2",
  star: "M12 2l2.9 6.2 6.6.8-4.9 4.5 1.3 6.5L12 16.8 6.1 20l1.3-6.5L2.5 9l6.6-.8z",
};
